/**
 * Design previews that need no photos.
 *
 * GAL-11.
 *   DesignCover   — a cover for a gallery with no photos yet: the
 *                   design's ground, the gallery name in its display
 *                   face, a rule in its accent. Used by hub cards so an
 *                   empty gallery still looks designed.
 *   DesignPreview — the live swatch beside the customize controls:
 *                   the design itself, rendered with the gallery's
 *                   name (GAL-14), over a type + colour strip that
 *                   reacts to the studio's choices.
 */
import type { CSSProperties } from 'react';
import { cn } from '@/lib/utils';
import { fontStack, type GalleryTheme } from '../templates/themes';
import { safeFontFamily, type GalleryCustomization } from '../templates/types';
import { resolveTheme } from '../templates/useGalleryEngine';
import { effectiveAccent } from './GalleryCustomizeControls';
import { GalleryDesignThumb, type ThumbPhoto } from './GalleryDesignThumb';

// SEC-XSS — the editor's own preview, so the attacker and the victim are
// the same studio and the stakes are far lower than the public renderer.
// Validated anyway: it builds the same quoted font stack, and leaving one
// unchecked copy of this pattern around is how the next one gets written.
const displayStack = (theme: GalleryTheme, c?: GalleryCustomization) => {
  const family = safeFontFamily(c?.fonts?.display);
  return family ? `'${family}', ${theme.display.fallback}` : fontStack(theme.display);
};
const bodyStack = (theme: GalleryTheme, c?: GalleryCustomization) => {
  const family = safeFontFamily(c?.fonts?.body);
  return family ? `'${family}', ${theme.body.fallback}` : fontStack(theme.body);
};

/** WCAG relative luminance of a #rrggbb colour; mid-grey for anything else. */
const luminance = (hex: string): number => {
  const m = /^#([0-9a-f]{6})$/i.exec(hex);
  if (!m) return 0.2;
  const n = parseInt(m[1], 16);
  const [r, g, b] = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

/**
 * GALLERY-2 — the accent stays the design's when the ground changes, so
 * a near-black accent (Basic's) can land on Charcoal or Black and vanish.
 * Where it would, the swatch uses the ground's ink instead.
 */
const visibleOn = (color: string, bg: string, ink: string): string => {
  const a = luminance(color);
  const b = luminance(bg);
  return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05) < 1.6 ? ink : color;
};

export function DesignCover({
  theme,
  name,
  customization,
  brandColor = null,
  className,
  style,
}: {
  theme: GalleryTheme;
  name: string;
  customization?: GalleryCustomization;
  brandColor?: string | null;
  className?: string;
  style?: CSSProperties;
}) {
  // GALLERY-2 — the studio's chosen ground and ink, as the design shows them.
  const ground = resolveTheme(theme, customization ?? {}, brandColor);
  const accent = visibleOn(effectiveAccent(theme, customization ?? {}, brandColor), ground.bg, ground.ink);
  return (
    <div
      className={cn('relative flex h-full w-full flex-col justify-end overflow-hidden p-5', className)}
      style={{ background: ground.bg, color: ground.ink, ...style }}
      aria-hidden
    >
      <span className="absolute left-5 top-4 text-[10px] uppercase tracking-[0.22em]" style={{ color: ground.muted }}>
        {theme.number} · {theme.name}
      </span>
      <div className="mb-3 h-px w-8" style={{ background: accent }} />
      <span
        className="line-clamp-3 text-2xl leading-[1.05] tracking-tight"
        style={{ fontFamily: displayStack(theme, customization), fontWeight: theme.display.weights?.[0] ?? 400 }}
      >
        {name}
      </span>
    </div>
  );
}

export function DesignPreview({
  theme,
  name,
  customization,
  brandColor,
  studioName,
  logoUrl,
  photos,
  className,
}: {
  theme: GalleryTheme;
  name: string;
  customization: GalleryCustomization;
  brandColor: string | null;
  studioName?: string | null;
  logoUrl?: string | null;
  photos?: ThumbPhoto[];
  className?: string;
}) {
  // GALLERY-2 — the strip sits on the ground the live design above it
  // is on, so a Background change reads as one surface, not two.
  const ground = resolveTheme(theme, customization, brandColor);
  const accent = visibleOn(effectiveAccent(theme, customization, brandColor), ground.bg, ground.ink);
  const title = name.trim() || 'Your gallery';
  return (
    <div className={cn('overflow-hidden rounded-2xl border border-border/50 bg-card shadow-sm', className)}>
      {/* GAL-14 — the real design, live, with the name typed above it. */}
      <GalleryDesignThumb
        template={theme.id}
        name={title}
        studioName={studioName}
        logoUrl={logoUrl}
        brandColor={brandColor}
        customization={customization}
        photos={photos}
        className="relative aspect-[16/10]"
      />
      <div className="space-y-3 p-5" style={{ background: ground.bg, color: ground.ink }}>
        <div className="flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-[0.22em]" style={{ color: ground.muted }}>
            {theme.number} · {theme.name}
          </span>
          <span className="h-3 w-3 rounded-full" style={{ background: accent }} aria-hidden />
        </div>
        <p
          className="line-clamp-2 text-3xl leading-[1.02] tracking-tight"
          style={{ fontFamily: displayStack(theme, customization), fontWeight: theme.display.weights?.[0] ?? 400 }}
        >
          {title}
        </p>
        <p className="text-[13px] leading-relaxed" style={{ fontFamily: bodyStack(theme, customization), color: ground.muted }}>
          Tap the heart on your favorites — they're shared as you pick them.
        </p>
        <div className="flex items-center gap-2 pt-1">
          <span
            className="rounded-full px-3 py-1 text-[11px]"
            style={{ background: ground.ink, color: ground.bg, fontFamily: bodyStack(theme, customization) }}
          >
            Download all
          </span>
          <span
            className="rounded-full border px-3 py-1 text-[11px]"
            style={{ borderColor: accent, color: accent, fontFamily: bodyStack(theme, customization) }}
          >
            Slideshow
          </span>
        </div>
      </div>
    </div>
  );
}

export default DesignPreview;
